import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_TARGET_AUDIENCE } from '@employ-engage/common-components';

type AudienceMember = {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  emailProblem: 'missing' | 'invalid' | 'reserved' | 'bounced' | null;
};

export interface TargetReachProps {
  /** The target as `TargetInput`: individuals as ids, roles as `{ id, name }`. */
  target: Record<string, unknown>;
  namesAnyone: boolean;
  count?: number;
  unreachable?: number;
  /** The target's own validation message, shown in place of the count. */
  error?: string;
  /**
   * How this audience is contacted. A bad email address says nothing about
   * whether a text arrives, so for 'text' the email problems are left out.
   */
  channel?: 'email' | 'text';
}

/**
 * Who a target reaches, under its picker: a count, how many of those
 * addresses are unlikely to receive anything, and — on request — the people
 * themselves, problems first.
 *
 * A count alone does not show that 90 of 97 are seeded test addresses that can
 * only hard-bounce, and that is exactly what went unnoticed before.
 */
export function TargetReach({
  target,
  namesAnyone,
  count,
  unreachable = 0,
  error,
  channel = 'email',
}: TargetReachProps) {
  const showEmail = channel === 'email';
  const { t } = useTranslation(['common']);
  const [expanded, setExpanded] = useState(false);
  const { data, loading } = useQuery(GET_TARGET_AUDIENCE, {
    variables: { target },
    skip: !expanded || !namesAnyone,
  });
  const people: AudienceMember[] = data?.targetAudience ?? [];

  const problemLabel = (problem: AudienceMember['emailProblem']) => {
    switch (problem) {
      case 'missing':
        return t('No email address');
      case 'invalid':
        return t('Not a valid address');
      case 'reserved':
        return t("Test address that can't receive mail");
      case 'bounced':
        return t('Bounced before');
      default:
        return null;
    }
  };

  if (error) {
    return (
      <p className="mt-2 text-sm text-red-500" role="alert">
        {error}
      </p>
    );
  }
  if (!namesAnyone || count === undefined) {
    return null;
  }
  if (count === 0) {
    return (
      <p className="mt-2 text-sm text-amber-600" role="status">
        {t('Reaches nobody right now: no active employee matches this target.')}
      </p>
    );
  }

  return (
    <div className="mt-2 text-sm">
      <p className="text-gray-500" role="status">
        {t('Active employees targeted: {{count}}', { count })}
        {showEmail && unreachable > 0 && (
          <span className="text-amber-600">
            {' · '}
            {t('{{count}} unlikely to receive email', { count: unreachable })}
          </span>
        )}{' '}
        <button
          type="button"
          className="text-indigo-600 hover:text-indigo-800 underline"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? t('Hide') : t('Show who')}
        </button>
      </p>
      {expanded && (
        <div className="mt-2 max-h-64 overflow-auto rounded-md border border-gray-200">
          {loading && !data ? (
            <p className="p-2 text-gray-500">{t('Loading...')}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {people.map((person) => (
                <li
                  key={person.id}
                  className="flex flex-wrap items-baseline justify-between gap-x-4 px-3 py-1.5"
                >
                  <span className="text-gray-900">
                    {person.firstName} {person.lastName}{' '}
                    {showEmail && (
                      <span className="text-gray-500">
                        {person.emailAddress || '—'}
                      </span>
                    )}
                  </span>
                  {showEmail && person.emailProblem && (
                    <span className="text-xs font-medium text-amber-700">
                      {problemLabel(person.emailProblem)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default TargetReach;
